/**
 * Domino action type registry.
 * Maps action types to their UI metadata for the flow builder.
 * At execution time, action_type maps to a primitive name in PRIMITIVE_CATALOG.
 */

/** All action types available for dominos in the flow builder */
export type DominoActionType =
  // LLM (new)
  | 'llm.prompt'
  | 'llm.extract'
  // Content processing (existing)
  | 'content.extract'
  | 'content.synthesize'
  | 'analysis.insight'
  // Web (existing)
  | 'web.extract'
  | 'exa.search'
  // CRM (existing)
  | 'crm.search_contact'
  | 'crm.search_company'
  | 'crm.plan_upsert_contact'
  | 'crm.apply_upsert_contact'
  // Email (new)
  | 'gmail.send'
  | 'gmail.draft'
  // Calendar (new)
  | 'gcalendar.list_events'
  | 'gcalendar.create_event'
  // Drive (new)
  | 'gdrive.read'
  | 'gdrive.list'
  | 'gsheet.create'
  | 'gsheet.append'
  // HITL (existing)
  | 'hitl.ask_user';

/** Config field definition for the builder UI */
export interface ActionConfigField {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'boolean' | 'number' | 'json';
  required: boolean;
  description?: string;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
}

/** UI metadata for an action type, used in the flow builder */
export interface DominoActionMeta {
  type: DominoActionType;
  label: string;
  description: string;
  category: 'AI' | 'Web' | 'CRM' | 'Email' | 'Calendar' | 'Drive' | 'Messaging' | 'Analysis';
  icon: string;
  /** OAuth provider required (null = no integration needed) */
  requiredIntegration: string | null;
  /** Fields the user can configure in the builder */
  configFields: ActionConfigField[];
}

/** Registry of all action types with their UI metadata */
export const DOMINO_ACTION_REGISTRY: DominoActionMeta[] = [
  // --- AI ---
  {
    type: 'llm.prompt',
    label: 'Prompt IA',
    description: 'Envoyer un prompt libre à un LLM avec contexte optionnel',
    category: 'AI',
    icon: 'Sparkles',
    requiredIntegration: null,
    configFields: [
      { key: 'prompt', label: 'Prompt', type: 'textarea', required: true, placeholder: 'Résume le contenu suivant...' },
      { key: 'systemPrompt', label: 'Prompt système', type: 'textarea', required: false, description: 'Instructions contextuelles pour le LLM' },
    ],
  },
  {
    type: 'llm.extract',
    label: 'Extraction IA',
    description: 'Extraire des données structurées d\'un texte avec un LLM',
    category: 'AI',
    icon: 'FileSearch',
    requiredIntegration: null,
    configFields: [
      { key: 'instructions', label: 'Instructions', type: 'textarea', required: true, placeholder: 'Extrais le nom, email et entreprise...' },
      { key: 'outputSchema', label: 'Schéma de sortie (JSON)', type: 'json', required: false, description: 'Structure JSON attendue en sortie' },
    ],
  },

  // --- Content ---
  {
    type: 'content.extract',
    label: 'Extraction de contenu',
    description: 'Extraire des informations structurées (entités, faits, résumé) d\'un contenu',
    category: 'AI',
    icon: 'ScanText',
    requiredIntegration: null,
    configFields: [
      { key: 'instructions', label: 'Instructions d\'extraction', type: 'textarea', required: false, placeholder: 'Extrais les entités et les faits clés...' },
      { key: 'extractionType', label: 'Type d\'extraction', type: 'select', required: false, options: [
        { value: 'all', label: 'Tout' },
        { value: 'entities', label: 'Entités' },
        { value: 'facts', label: 'Faits' },
        { value: 'key_values', label: 'Clés-valeurs' },
        { value: 'summary', label: 'Résumé' },
      ]},
    ],
  },
  {
    type: 'content.synthesize',
    label: 'Résumé',
    description: 'Résumer ou synthétiser du contenu textuel',
    category: 'AI',
    icon: 'FileText',
    requiredIntegration: null,
    configFields: [
      { key: 'instructions', label: 'Instructions de synthèse', type: 'textarea', required: false, placeholder: 'Résume en 5 bullet points...' },
    ],
  },
  {
    type: 'analysis.insight',
    label: 'Analyse',
    description: 'Dériver des insights à partir de données',
    category: 'Analysis',
    icon: 'Lightbulb',
    requiredIntegration: null,
    configFields: [
      {
        key: 'analysisType',
        label: 'Type d’analyse',
        type: 'select',
        required: false,
        options: [
          { value: 'commercial_angles', label: 'Angles commerciaux' },
          { value: 'competitive_intelligence', label: 'Veille concurrentielle' },
          { value: 'lead_qualification', label: 'Qualification lead' },
          { value: 'risk_assessment', label: 'Évaluation risques' },
          { value: 'opportunity_discovery', label: 'Détection opportunités' },
          { value: 'general', label: 'Général' },
        ],
      },
      {
        key: 'userContext',
        label: 'Contexte utilisateur (JSON)',
        type: 'json',
        required: false,
        description: 'Objet optionnel: role, goal, company, industry',
      },
      {
        key: 'additionalContext',
        label: 'Contexte additionnel (JSON)',
        type: 'json',
        required: false,
        description: 'Objet libre pour enrichir l’analyse',
      },
      {
        key: 'sourceUrl',
        label: 'URL source',
        type: 'text',
        required: false,
      },
    ],
  },

  // --- Web ---
  {
    type: 'exa.search',
    label: 'Recherche web',
    description: 'Recherche web neurale via Exa.ai',
    category: 'Web',
    icon: 'Search',
    requiredIntegration: null,
    configFields: [
      { key: 'query', label: 'Requête', type: 'text', required: true, placeholder: 'CEO de {{input.company}}' },
      { key: 'numResults', label: 'Nombre de résultats', type: 'number', required: false },
    ],
  },
  {
    type: 'web.extract',
    label: 'Extraction web',
    description: 'Extraire des données structurées d\'une page web',
    category: 'Web',
    icon: 'Globe',
    requiredIntegration: null,
    configFields: [
      { key: 'query', label: 'Que extraire ?', type: 'textarea', required: true, placeholder: 'Les postes ouverts avec titre et localisation' },
    ],
  },

  // --- CRM ---
  {
    type: 'crm.search_contact',
    label: 'Rechercher un contact',
    description: 'Chercher un contact dans le CRM',
    category: 'CRM',
    icon: 'UserSearch',
    requiredIntegration: 'hubspot',
    configFields: [
      { key: 'query', label: 'Recherche', type: 'text', required: true, placeholder: '{{input.contact_email}}' },
    ],
  },
  {
    type: 'crm.search_company',
    label: 'Rechercher une entreprise',
    description: 'Chercher une entreprise dans le CRM',
    category: 'CRM',
    icon: 'Building',
    requiredIntegration: 'hubspot',
    configFields: [
      { key: 'query', label: 'Recherche', type: 'text', required: true, placeholder: '{{input.company_name}}' },
    ],
  },
  {
    type: 'crm.plan_upsert_contact',
    label: 'Préparer mise à jour CRM',
    description: 'Planifier la création ou mise à jour d\'un contact (preview sans écriture)',
    category: 'CRM',
    icon: 'ClipboardList',
    requiredIntegration: 'hubspot',
    configFields: [],
  },
  {
    type: 'crm.apply_upsert_contact',
    label: 'Appliquer mise à jour CRM',
    description: 'Exécuter la création/mise à jour planifiée dans le CRM',
    category: 'CRM',
    icon: 'CheckCircle',
    requiredIntegration: 'hubspot',
    configFields: [],
  },

  // --- Email ---
  {
    type: 'gmail.send',
    label: 'Envoyer un email',
    description: 'Envoyer un email via Gmail',
    category: 'Email',
    icon: 'Send',
    requiredIntegration: 'gmail',
    configFields: [
      { key: 'to', label: 'Destinataire', type: 'text', required: true, placeholder: '{{input.email}}' },
      { key: 'subject', label: 'Objet', type: 'text', required: true },
      { key: 'body', label: 'Corps du mail', type: 'textarea', required: true },
    ],
  },
  {
    type: 'gmail.draft',
    label: 'Créer un brouillon',
    description: 'Créer un brouillon dans Gmail',
    category: 'Email',
    icon: 'FileEdit',
    requiredIntegration: 'gmail',
    configFields: [
      { key: 'to', label: 'Destinataire', type: 'text', required: true },
      { key: 'subject', label: 'Objet', type: 'text', required: true },
      { key: 'body', label: 'Corps du mail', type: 'textarea', required: true },
    ],
  },

  // --- Calendar ---
  {
    type: 'gcalendar.list_events',
    label: 'Lister des événements',
    description: 'Lister les événements Google Calendar sur une plage horaire',
    category: 'Calendar',
    icon: 'Calendar',
    requiredIntegration: 'google_calendar',
    configFields: [
      { key: 'calendarId', label: 'Calendrier (ID)', type: 'text', required: false, placeholder: 'primary' },
      { key: 'timeMin', label: 'Début', type: 'text', required: true, placeholder: 'mardi prochain 9h' },
      { key: 'timeMax', label: 'Fin', type: 'text', required: true, placeholder: 'mardi prochain 18h' },
      { key: 'query', label: 'Recherche (optionnel)', type: 'text', required: false, placeholder: 'demo client' },
      { key: 'maxResults', label: 'Max résultats', type: 'number', required: false },
    ],
  },
  {
    type: 'gcalendar.create_event',
    label: 'Créer un événement',
    description: 'Créer un événement Google Calendar (horaire)',
    category: 'Calendar',
    icon: 'CalendarPlus',
    requiredIntegration: 'google_calendar',
    configFields: [
      { key: 'calendarId', label: 'Calendrier (ID)', type: 'text', required: false, placeholder: 'primary' },
      { key: 'summary', label: 'Titre', type: 'text', required: true, placeholder: 'Rendez-vous client' },
      { key: 'description', label: 'Description', type: 'textarea', required: false },
      { key: 'location', label: 'Lieu', type: 'text', required: false, placeholder: 'Visio / Paris' },
      { key: 'start', label: 'Début', type: 'text', required: true, placeholder: 'mardi prochain 10h' },
      { key: 'end', label: 'Fin', type: 'text', required: true, placeholder: 'il dure 2h' },
      { key: 'timeZone', label: 'Fuseau horaire', type: 'text', required: false, placeholder: 'Europe/Paris' },
      { key: 'attendees', label: 'Participants', type: 'text', required: false, placeholder: 'alice@example.com; bob@example.com' },
    ],
  },

  // --- Drive ---
  {
    type: 'gdrive.read',
    label: 'Lire un fichier Drive',
    description: 'Lire le contenu d\'un fichier Google Drive',
    category: 'Drive',
    icon: 'FileDown',
    requiredIntegration: 'google_workspace',
    configFields: [
      { key: 'fileId', label: 'ID du fichier', type: 'text', required: true },
    ],
  },
  {
    type: 'gdrive.list',
    label: 'Lister des fichiers Drive',
    description: 'Chercher des fichiers dans Google Drive',
    category: 'Drive',
    icon: 'FolderSearch',
    requiredIntegration: 'google_workspace',
    configFields: [
      { key: 'query', label: 'Recherche', type: 'text', required: true, placeholder: 'Notes de réunion {{input.date}}' },
    ],
  },
  {
    type: 'gsheet.create',
    label: 'Créer un Google Sheet',
    description: 'Créer un Sheet et y écrire un tableau de données',
    category: 'Drive',
    icon: 'Sheet',
    requiredIntegration: 'google_workspace',
    configFields: [
      { key: 'title', label: 'Titre du Sheet', type: 'text', required: true, placeholder: 'Leads semaine 7' },
      { key: 'sheetName', label: 'Nom de l\'onglet', type: 'text', required: false, placeholder: 'Sheet1' },
      { key: 'folderId', label: 'Dossier Drive (ID)', type: 'text', required: false },
    ],
  },
  {
    type: 'gsheet.append',
    label: 'Ajouter des lignes à un Sheet',
    description: 'Ajouter des lignes à un Google Sheet existant',
    category: 'Drive',
    icon: 'FileSpreadsheet',
    requiredIntegration: 'google_workspace',
    configFields: [
      { key: 'spreadsheetId', label: 'ID du Sheet', type: 'text', required: true },
      { key: 'sheetName', label: 'Nom de l\'onglet', type: 'text', required: false, placeholder: 'Sheet1' },
    ],
  },

  // --- HITL ---
  {
    type: 'hitl.ask_user',
    label: 'Question utilisateur',
    description: 'Poser une question à l\'utilisateur et attendre sa réponse',
    category: 'AI',
    icon: 'HelpCircle',
    requiredIntegration: null,
    configFields: [
      { key: 'question', label: 'Question', type: 'textarea', required: true },
      { key: 'inputType', label: 'Type de réponse', type: 'select', required: false, options: [
        { value: 'text', label: 'Texte libre' },
        { value: 'yes_no', label: 'Oui / Non' },
        { value: 'single_choice', label: 'Choix unique' },
      ]},
    ],
  },
];

/** Get action metadata by type */
export const getActionMeta = (type: string): DominoActionMeta | undefined =>
  DOMINO_ACTION_REGISTRY.find(a => a.type === type);
